
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../../lib/supabaseClient';

interface Notification {
  id: string;
  title: string;
  message: string;
  type: string;
  job_id: string | null;
  is_read: boolean;
  created_at: string;
}

interface NotificationsProps {
  onUnreadChange: (count: number) => void;
}

export default function Notifications({ onUnreadChange }: NotificationsProps) {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchNotifications();

    // Subscribe to new notifications
    const subscription = supabase
      .channel('client_notifications_changes')
      .on('postgres_changes', 
        { event: 'INSERT', schema: 'public', table: 'client_notifications' }, 
        () => {
          fetchNotifications();
        }
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const fetchNotifications = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from('client_notifications')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(50);

      if (error) throw error;

      setNotifications(data || []);
      
      const unreadCount = data?.filter(n => !n.is_read).length || 0;
      onUnreadChange(unreadCount);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (notificationId: string) => {
    try {
      await supabase
        .from('client_notifications')
        .update({ is_read: true })
        .eq('id', notificationId);

      setNotifications(notifications.map(n => 
        n.id === notificationId ? { ...n, is_read: true } : n
      ));

      const unreadCount = notifications.filter(n => !n.is_read && n.id !== notificationId).length;
      onUnreadChange(unreadCount);
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      await supabase
        .from('client_notifications')
        .update({ is_read: true })
        .eq('user_id', user.id)
        .eq('is_read', false);

      setNotifications(notifications.map(n => ({ ...n, is_read: true })));
      onUnreadChange(0);
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'job_accepted': return 'ri-check-line';
      case 'provider_assigned': return 'ri-user-add-line';
      case 'provider_en_route': return 'ri-car-line';
      case 'job_started': return 'ri-play-line';
      case 'job_completed': return 'ri-checkbox-circle-line';
      case 'payment_required': return 'ri-money-pound-circle-line';
      case 'review_request': return 'ri-star-line';
      default: return 'ri-notification-line';
    }
  };

  const getNotificationColor = (type: string) => {
    switch (type) {
      case 'job_accepted': return 'bg-green-100 text-green-600';
      case 'provider_assigned': return 'bg-blue-100 text-blue-600';
      case 'provider_en_route': return 'bg-purple-100 text-purple-600';
      case 'job_started': return 'bg-orange-100 text-orange-600';
      case 'job_completed': return 'bg-green-100 text-green-600';
      case 'payment_required': return 'bg-yellow-100 text-yellow-600';
      case 'review_request': return 'bg-pink-100 text-pink-600';
      default: return 'bg-gray-100 text-gray-600';
    }
  };

  const handleNotificationClick = (notification: Notification) => {
    markAsRead(notification.id);
    if (notification.job_id) {
      navigate(`/job-tracking/${notification.job_id}`);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <i className="ri-loader-4-line text-4xl text-blue-600 animate-spin"></i>
      </div>
    );
  }

  return (
    <div>
      {notifications.length > 0 && notifications.some(n => !n.is_read) && (
        <div className="bg-white rounded-xl shadow-sm p-4 mb-6 flex justify-between items-center">
          <p className="text-gray-700">
            <span className="font-bold">{notifications.filter(n => !n.is_read).length}</span> unread notifications
          </p>
          <button
            onClick={markAllAsRead}
            className="px-4 py-2 bg-[#0B3C5D] text-white rounded-lg hover:bg-[#082A42] transition-colors whitespace-nowrap cursor-pointer font-semibold"
          >
            Mark All as Read
          </button>
        </div>
      )}

      {notifications.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm p-12 text-center">
          <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <i className="ri-notification-line text-4xl text-gray-400"></i>
          </div>
          <h3 className="text-xl font-bold text-gray-900 mb-2">No Notifications</h3>
          <p className="text-gray-600">You're all caught up!</p>
        </div>
      ) : (
        <div className="space-y-3">
          {notifications.map((notification) => (
            <div
              key={notification.id}
              onClick={() => handleNotificationClick(notification)}
              className={`bg-white rounded-xl shadow-sm p-5 hover:shadow-md transition-all cursor-pointer ${
                !notification.is_read ? 'border-l-4 border-[#FF6A00]' : ''
              }`}
            >
              <div className="flex items-start gap-4">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 ${getNotificationColor(notification.type)}`}>
                  <i className={`${getNotificationIcon(notification.type)} text-2xl`}></i>
                </div>
                <div className="flex-1">
                  <div className="flex items-start justify-between mb-1">
                    <h4 className="font-bold text-gray-900">{notification.title}</h4>
                    {!notification.is_read && (
                      <span className="w-2 h-2 bg-[#FF6A00] rounded-full flex-shrink-0 mt-2"></span>
                    )}
                  </div>
                  <p className="text-gray-600 text-sm mb-2">{notification.message}</p>
                  <p className="text-xs text-gray-500">
                    {new Date(notification.created_at).toLocaleString('en-GB', {
                      day: 'numeric',
                      month: 'short',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
